import React from 'react';
import PropTypes from 'prop-types';

/**
 * ClaimErrorBoundary - A reusable error boundary component for handling unexpected rendering errors
 * 
 * This component catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * 
 * Note: Error boundaries only catch errors in:
 * - Rendering
 * - Lifecycle methods
 * - Constructors of the whole tree below them
 * 
 * They do NOT catch errors in:
 * - Event handlers
 * - Asynchronous code (e.g., setTimeout or requestAnimationFrame callbacks)
 * - Errors thrown in the error boundary itself (rather than its children)
 */
class ClaimErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  /**
   * Static method called when an error is thrown during rendering
   * Updates state to trigger fallback UI
   */
  static getDerivedStateFromError(error) {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  /**
   * Lifecycle method called when an error is caught
   * Used for logging and error reporting
   */
  componentDidCatch(error, errorInfo) {
    // Update state with detailed error information
    this.setState({
      error,
      errorInfo
    });

    // Log error details for debugging
    this.logError(error, errorInfo);
    
    // Report to external monitoring service (if configured)
    this.reportError(error, errorInfo);
  }

  /**
   * Log error details to console and/or external logging service
   */
  logError = (error, errorInfo) => {
    const { errorId } = this.state;
    const { componentName = 'Unknown Component' } = this.props;
    
    // Console logging for development
    if (import.meta.env.DEV) {
      console.group(`🚨 Error Boundary Caught Error in ${componentName}`);
      console.error('Error ID:', errorId);
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo?.componentStack);
      console.groupEnd();
    }

    // TODO: Send to logging service (Winston, Sentry, etc.)
    // Example for future implementation:
    // logger.error('UI Error Caught by Error Boundary', {
    //   errorId,
    //   componentName,
    //   message: error.message,
    //   stack: error.stack,
    //   componentStack: errorInfo?.componentStack,
    //   timestamp: new Date().toISOString(),
    //   userAgent: navigator.userAgent,
    //   url: window.location.href
    // });
  };

  /**
   * Report error to external monitoring service
   */
  reportError = (error, errorInfo) => {
    const { errorId } = this.state;
    const { componentName = 'Unknown Component' } = this.props;

    // TODO: Implement external error reporting
    // Example for Sentry integration:
    // if (window.Sentry) {
    //   window.Sentry.withScope((scope) => {
    //     scope.setTag('errorBoundary', true);
    //     scope.setTag('componentName', componentName);
    //     scope.setContext('errorInfo', {
    //       errorId,
    //       componentStack: errorInfo?.componentStack
    //     });
    //     window.Sentry.captureException(error);
    //   });
    // }

    // For now, we'll use a simple API call to log errors
    try {
      // Only report in production to avoid noise during development
      if (import.meta.env.PROD) {
        fetch('/api/errors/report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            errorId,
            componentName,
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo?.componentStack,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent
          })
        }).catch(reportError => {
          // Silent fail for error reporting to avoid infinite loops
          console.warn('Failed to report error to server:', reportError);
        });
      }
    } catch (reportError) {
      // Silent fail to prevent error boundary from crashing
      console.warn('Error in error reporting:', reportError);
    }
  };

  /**
   * Reset error boundary state to try rendering again
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  /**
   * Refresh the entire page as a last resort
   */
  handleRefresh = () => {
    window.location.reload();
  };

  /**
   * Render fallback UI when error occurs
   */
  renderFallbackUI() {
    const { 
      fallbackMessage, 
      showDetails = false,
      showRefreshButton = true,
      showResetButton = true,
      componentName = 'this section'
    } = this.props;
    
    const { error, errorId } = this.state;

    const defaultMessage = `Something went wrong in ${componentName}. Please refresh the page or contact support if the problem persists.`;

    return (
      <div className="min-h-[400px] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-red-200 p-6 text-center">
          {/* Error Icon */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg 
              className="h-6 w-6 text-red-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>

          {/* Error Title */}
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Oops! Something went wrong
          </h3>

          {/* Error Message */}
          <p className="text-sm text-gray-600 mb-6">
            {fallbackMessage || defaultMessage}
          </p>

          {/* Error ID for Support */}
          {errorId && (
            <p className="text-xs text-gray-400 mb-4">
              Error ID: {errorId}
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {showResetButton && (
              <button
                onClick={this.handleReset}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Try Again
              </button>
            )}
            {showRefreshButton && (
              <button
                onClick={this.handleRefresh}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Refresh Page
              </button>
            )}
          </div>

          {/* Error Details (Development Only) */}
          {showDetails && import.meta.env.DEV && error && (
            <details className="mt-6 text-left">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                Technical Details (Development)
              </summary>
              <div className="mt-2 p-3 bg-gray-50 rounded text-xs font-mono text-red-600 overflow-auto max-h-32">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                {error.stack && (
                  <div>
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap">{error.stack}</pre>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Contact Support Link */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Need help? {' '}
              <a 
                href="mailto:support@oldmutual.com" 
                className="text-blue-600 hover:text-blue-500 underline"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return this.renderFallbackUI();
    }

    return this.props.children;
  }
}

ClaimErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackMessage: PropTypes.string,
  componentName: PropTypes.string,
  showDetails: PropTypes.bool,
  showRefreshButton: PropTypes.bool,
  showResetButton: PropTypes.bool,
};

ClaimErrorBoundary.defaultProps = {
  fallbackMessage: null,
  componentName: 'this section',
  showDetails: false,
  showRefreshButton: true,
  showResetButton: true,
};

export default ClaimErrorBoundary;